import React from 'react'
import { cn } from '../../lib/utils'
import { useTheme } from '../../contexts/ThemeContext'
import { useMobile } from '../../contexts/MobileContext'

interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: 'primary' | 'secondary' | 'destructive' | 'ghost' | 'outline' | 'link' | 'info' | 'success' | 'warning' | 'error'
  size?: 'sm' | 'md' | 'lg' | 'icon'
  children: React.ReactNode
  glass?: boolean
}

export function Button({ 
  variant = 'primary', 
  size = 'md', 
  className, 
  children, 
  glass = false,
  ...props 
}: ButtonProps) {
  const { theme } = useTheme()
  const { isMobile } = useMobile()
  const isDark = theme === 'dark' || (theme === 'system' && window.matchMedia('(prefers-color-scheme: dark)').matches)

  const baseClasses = 'inline-flex items-center justify-center rounded-md font-medium transition-all duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-interactive focus-visible:ring-offset-2 disabled:opacity-50 disabled:pointer-events-none'
  
  const variants = {
    primary: 'bg-interactive text-white hover:bg-interactive-hover shadow-md hover:shadow-lg',
    secondary: 'bg-interactive-secondary text-content border border-outline hover:bg-interactive-secondary-hover hover:border-outline-secondary',
    destructive: 'bg-error text-white hover:bg-error/90 shadow-md hover:shadow-lg',
    ghost: 'hover:bg-surface-secondary hover:text-content',
    outline: 'border border-outline bg-background hover:bg-surface-secondary hover:text-content',
    link: 'text-interactive underline-offset-4 hover:underline',
    info: 'bg-info text-white hover:bg-info/90 shadow-md hover:shadow-lg',
    success: 'bg-success text-white hover:bg-success/90 shadow-md hover:shadow-lg',
    warning: 'bg-warning text-white hover:bg-warning/90 shadow-md hover:shadow-lg',
    error: 'bg-error text-white hover:bg-error/90 shadow-md hover:shadow-lg'
  }
  
  const sizes = {
    sm: 'h-8 px-3 text-sm',
    md: 'h-10 px-4 py-2',
    lg: 'h-12 px-8 text-lg',
    icon: 'h-10 w-10 p-0'
  }

  const getGlassClasses = () => {
    if (!glass || !isDark || isMobile) return ''
    return 'bg-white/10 backdrop-blur-sm'
  }

  return (
    <button
      className={cn(
        baseClasses,
        variants[variant],
        sizes[size],
        getGlassClasses(),
        className
      )}
      {...props}
    >
      {children}
    </button>
  )
}
